// src/pages/Onboarding.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import QRCode from "react-qr-code";
import { Eye, EyeOff, ClipboardPaste, X } from "lucide-react";

/* ──────────────────────────────
   Minimal inline Accordion (no deps)
   ────────────────────────────── */
function LiteAccordionItem({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details
      className="border rounded-lg bg-white open:shadow-sm transition-shadow"
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none list-none px-3 py-2 font-medium flex items-center justify-between">
        <span>{title}</span>
        <span className="ml-4 text-gray-400">▾</span>
      </summary>
      <div className="px-4 pb-4 pt-2 text-sm text-gray-700 space-y-2">{children}</div>
    </details>
  );
}

/* ──────────────────────────────
   Minimal Modal (no deps)
   ────────────────────────────── */
function LiteModal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-4 text-sm text-gray-700">{children}</div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────
   Privacy Policy Content (edit as needed)
   ────────────────────────────── */
function PrivacyPolicyBody() {
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Effective Date: {new Date().toLocaleDateString()}</p>

      <p>
        At <strong>InfuseED</strong>, we take privacy seriously. This policy explains what we collect,
        why we collect it, and what we do (and don’t do) with it.
      </p>

      <h4 className="font-semibold">1. Information We Collect</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Organization info</strong> you provide (school name, Canvas base URL, contact details).</li>
        <li><strong>Authentication tokens</strong> you provide to connect to Canvas.</li>
        <li><strong>Usage data</strong> like timestamps and status codes for debugging and security.</li>
      </ul>
      <p>We do not collect student grades or personally identifiable student data unless a feature explicitly requires it and you enable it.</p>

      <h4 className="font-semibold">2. How We Use Data</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>To connect InfuseED to your Canvas instance and provide requested features.</li>
        <li>To troubleshoot, secure, and improve the service.</li>
      </ul>
      <p>We do not sell your data or use it for marketing without your permission.</p>

      <h4 className="font-semibold">3. Security</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>Tokens are encrypted in transit and at rest (TLS 1.2+, AES-256).</li>
        <li>Access to tokens is restricted; we follow least-privilege principles.</li>
        <li>Only backend services access tokens; staff cannot view tokens in plain text.</li>
      </ul>

      <h4 className="font-semibold">4. Sharing</h4>
      <p>We don’t share data except when required by law or to operate the service (e.g., calling Canvas APIs).</p>

      <h4 className="font-semibold">5. Your Choices</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>Update org details any time in <em>Organization Profile</em>.</li>
        <li>Revoke your token in Canvas to immediately cut off access.</li>
        <li>Request deletion: <a className="text-blue-600 underline" href="mailto:support@getinfused.net">support@getinfused.net</a>.</li>
      </ul>

      <h4 className="font-semibold">6. Changes</h4>
      <p>We’ll update this page when the policy changes and notify you of material updates.</p>

      <h4 className="font-semibold">7. Contact</h4>
      <p>Questions? <a className="text-blue-600 underline" href="mailto:support@getinfused.net">support@getinfused.net</a></p>
    </div>
  );
}

type Me = {
  id: number;
  email: string;
  role: "admin" | "waitlist" | string;
  onboarding_stage: "start" | "profile" | "2fa" | "canvas" | "complete";
  twofa_enabled: boolean;
};

export default function Onboarding() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = async () => {
    const res = await api.get("/auth/me");
    setMe(res.data);
    setLoading(false);
  };

  useEffect(() => {
    refresh().catch(() => {
      navigate("/", { replace: true });
    });
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    if (me.role !== "admin") {
      navigate("/verify-reminder", { replace: true, state: { email: me.email } });
      return;
    }
    if (me.onboarding_stage === "complete") {
      navigate("/app", { replace: true });
    }
  }, [me, navigate]);

  if (loading || !me) return null;

  switch (me.onboarding_stage) {
    case "start":
    case "profile":
      return <ProfileStep onDone={refresh} />;
    case "2fa":
      return <TwoFAStep onDone={refresh} email={me.email} />;
    case "canvas":
      return <CanvasStep onDone={refresh} />;
    default:
      return null;
  }
}

/* ---------------------- Profile Step ---------------------- */

function ProfileStep({ onDone }: { onDone: () => void }) {
  const [orgName, setOrgName] = useState("");
  const [canvasUrl, setCanvasUrl] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!orgName || !canvasUrl || !contactName) {
      setErr("Please fill out all required fields.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/onboarding/profile", {
        org_name: orgName.trim(),
        canvas_base_url: canvasUrl.trim(),
        contact_name: contactName.trim(),
        contact_phone: contactPhone.trim() || null,
      });
      await onDone();
    } catch (e: any) {
      console.error("❌ profile save failed", e);
      setErr(e?.response?.data?.detail || "Could not save organization profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4">
        <h1 className="text-3xl font-semibold">Tell us about your org</h1>
        {err && <p className="text-red-600 text-sm">{err}</p>}

        <Input placeholder="Organization name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
        <Input placeholder="Canvas base URL (https://...instructure.com)" value={canvasUrl} onChange={(e) => setCanvasUrl(e.target.value)} />
        <Input placeholder="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        <Input placeholder="Contact phone (optional)" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />

        <Button onClick={save} disabled={saving} className="w-full">
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------------- 2FA Step ---------------------- */

function TwoFAStep({ onDone, email }: { onDone: () => void; email: string }) {
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [okMsg, setOkMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.post("/onboarding/2fa/init");
        setOtpauth(res.data?.otpauth || null);
      } catch (e) {
        console.error("2FA init failed", e);
        setErr("Couldn’t initialize 2FA. Try reloading.");
      }
    })();
  }, []);

  const onCodeChange = (v: string) => {
    const next = v.replace(/\D/g, "").slice(0, 6);
    setCode(next);
    if (err) setErr("");
  };

  const verify = async () => {
    if (code.length !== 6) {
      setErr("Enter the 6-digit code from your app.");
      return;
    }
    try {
      setVerifying(true);
      await api.post("/onboarding/2fa/verify", { code });
      setOkMsg("2FA enabled — nice!");
      setErr("");
      setTimeout(() => onDone(), 900);
    } catch (e: any) {
      console.error("2FA verify failed", e);
      setErr(e?.response?.data?.detail || "Invalid code. Try again.");
      setOkMsg("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-5 text-center">
        <h1 className="text-3xl font-semibold">Set up 2FA</h1>
        <p className="text-gray-600">Scan this QR with Google Authenticator / Authy, or add manually.</p>

        <p className="text-xs text-gray-500">
          Securing account: <span className="font-mono">{email}</span>
        </p>

        {otpauth ? (
          <div className="flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg shadow">
              <QRCode value={otpauth} size={192} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Generating QR…</p>
        )}

        {otpauth && <div className="text-xs text-gray-500 break-all border rounded p-3">{otpauth}</div>}

        {okMsg && <div className="rounded-md bg-green-50 text-green-700 text-sm p-2">{okMsg}</div>}
        {err && <div className="rounded-md bg-red-50 text-red-700 text-sm p-2">{err}</div>}

        <Input
          placeholder="Enter 6-digit code"
          inputMode="numeric"
          pattern="\d*"
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          className="text-center tracking-widest"
          disabled={!!okMsg}
        />

        <Button onClick={verify} disabled={verifying || !!okMsg} className="w-full">
          {verifying ? "Verifying…" : "Verify & Continue"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------------- Canvas Step ---------------------- */

type CanvasTestResult = {
  ok: boolean;
  user?: { id?: number; name?: string; login_id?: string };
  admin_like_access?: boolean;
};

function CanvasStep({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [canvasBaseUrl, setCanvasBaseUrl] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<CanvasTestResult | null>(null);

  // Privacy modal + consent
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);

  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/onboarding/profile");
        const url = res.data?.canvas_base_url || null;
        setCanvasBaseUrl(url);
        setUrlDraft(url || "");
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setToken(text.trim());
    } catch {
      setErr("Couldn’t read from clipboard. Paste manually (Ctrl/Cmd+V).");
    }
  };

  const testConnection = async () => {
    setErr("");
    setOk("");
    setTestResult(null);
    if (!canvasBaseUrl) {
      setErr("Set your Canvas base URL first.");
      return;
    }
    
    const trimmed = token.trim();
    if (trimmed.length < 20) {
      setErr("That token looks too short. Paste your Canvas Admin Personal Access Token.");
      return;
    }
    try {
      setTesting(true);
      const res = await api.post("/onboarding/canvas/test", {
        base_url: canvasBaseUrl,
        token: trimmed,
      });
      setTestResult(res.data as CanvasTestResult);
      setOk(
        res.data?.admin_like_access
          ? "Canvas connection looks good (admin capabilities detected)."
          : "Token is valid, but admin-level endpoints may be restricted."
      );
    } catch (e: any) {
      console.error("Canvas test failed", e);
      setErr(e?.response?.data?.detail || "Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  const saveUrl = async () => {
    setErr("");
    try {
      const clean = urlDraft.trim().replace(/\/+$/, "");
      
      const res = await api.post("/onboarding/profile/canvas-url", { canvas_base_url: clean });
      setCanvasBaseUrl(res.data?.canvas_base_url || clean);
      setEditingUrl(false);
      setOk("Canvas URL updated.");
      setTestResult(null);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not update Canvas URL.");
    }
  };

  const save = async () => {
    setErr("");
    setOk("");
    const trimmed = token.trim();
    if (!testResult?.user) {
      setErr("Please test your connection before finishing.");
      return;
    }
    if (!policyAccepted) {
      setErr("Please agree to the Privacy Policy before finishing.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/onboarding/canvas/token", { token: trimmed });
      setOk("Canvas connected! Redirecting…");
      setTimeout(() => onDone(), 800);
    } catch (e: any) {
      console.error("Canvas token save failed", e);
      setErr(e?.response?.data?.detail || "Could not save token. Check and try again.");
    } finally {
      setSaving(false);
    }
  };

  const settingsHref = canvasBaseUrl ? canvasBaseUrl.replace(/\/+$/, "") + "/profile/settings" : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-2xl space-y-5">
        <h1 className="text-2xl font-semibold">Connect Canvas</h1>
        <p className="text-gray-600 text-sm">
          Paste an <strong>Admin Personal Access Token</strong> with the required scopes. We’ll move to LTI 1.3 soon.
        </p>

        {/* Privacy consent line */}
        <div className="rounded-md border p-3 bg-gray-50">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={policyAccepted}
              onChange={(e) => setPolicyAccepted(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <button type="button" className="text-blue-600 underline" onClick={() => setPolicyOpen(true)}>
                Privacy Policy
              </button>
              .
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Short version: we only use your token to perform the actions you request, we encrypt it, and you can revoke it anytime.
          </p>
        </div>

        {/* Modal with full policy */}
        <LiteModal open={policyOpen} onClose={() => setPolicyOpen(false)} title="Privacy Policy">
          <PrivacyPolicyBody />
        </LiteModal>

        {/* Canvas URL row */}
        <div className="rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm">
              <div className="font-medium">Canvas base URL</div>
              {!editingUrl ? (
                <div className="text-gray-700">{canvasBaseUrl || "Not set"}</div>
              ) : (
                <Input
                  placeholder="https://yourcollege.instructure.com"
                  value={urlDraft}
                  onChange={(e) => setUrlDraft(e.target.value)}
                />
              )}
            </div>
            {!editingUrl ? (
              <Button variant="secondary" onClick={() => setEditingUrl(true)}>
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => { setEditingUrl(false); setUrlDraft(canvasBaseUrl || ""); }}>
                  Cancel
                </Button>
                <Button onClick={saveUrl}>Save URL</Button>
              </div>
            )}
          </div>
          {settingsHref && (
            <div className="mt-2 text-xs">
              Quick link:{" "}
              <a className="text-blue-600 underline" href={settingsHref} target="_blank" rel="noreferrer">
                {settingsHref}
              </a>
            </div>
          )}
        </div>

        {/* Help accordion (lite) */}
        <div className="space-y-3">
          <LiteAccordionItem title="How to create a token (step-by-step)" defaultOpen>
            <ol className="list-decimal ml-5 space-y-1">
              <li>
                {settingsHref ? (
                  <>
                    Open Canvas settings →{" "}
                    <a className="text-blue-600 underline" href={settingsHref} target="_blank" rel="noreferrer">
                      {settingsHref}
                    </a>
                  </>
                ) : (
                  <>In Canvas, go to <em>Account → Settings</em>.</>
                )}
              </li>
              <li>Scroll to <em>Approved Integrations</em> and click <strong>Add New Access Token</strong>.</li>
              <li>Give it a name (e.g., “Infuse Admin”), optional expiration, then click <strong>Generate Token</strong>.</li>
              <li>Copy the token and paste it below.</li>
              <li>You can revoke it anytime from the same page.</li>
            </ol>
            <div className="mt-3">
              <div className="aspect-video w-full rounded-lg overflow-hidden border">
                <iframe
                  className="w-full h-full"
                  src="https://embed.app.guidde.com/playbooks/ciQA5DhbcXvgvwh6G8hVPk"
                  title="How do I manage API access tokens?"
                  frameBorder="0"
                  referrerPolicy="no-referrer"
                  allow="clipboard-write"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                  allowFullScreen
                />
              </div>
            </div>
          </LiteAccordionItem>

          <LiteAccordionItem title="How we use this">
            <ul className="list-disc ml-5 space-y-1">
              <li>We use your token to make Canvas API calls you authorize (e.g., automate enrollments, create courses, sync grades).</li>
              <li>We do <em>not</em> share or sell your token. We do <em>not</em> include it in logs or analytics.</li>
              <li>You can rotate or revoke the token in Canvas at any time; we’ll ask for a fresh one when needed.</li>
            </ul>
          </LiteAccordionItem>

          <LiteAccordionItem title="How you can feel confident this is secure">
            <ul className="list-disc ml-5 space-y-1">
              <li>Transport: sent only over HTTPS.</li>
              <li>Storage: encrypted at rest and access-controlled on our servers.</li>
              <li>Least privilege: use a dedicated Admin token with only the scopes you need and an expiration date.</li>
              <li>Control: revoke the token in Canvas instantly if you ever want to cut off access.</li>
            </ul>
          </LiteAccordionItem>
        </div>

        {ok && <div className="rounded bg-green-50 text-green-700 text-sm p-2">{ok}</div>}
        {err && <div className="rounded bg-red-50 text-red-700 text-sm p-2">{err}</div>}

        {/* Token input with mask + paste helper */}
        <div className="flex items-center gap-2">
          <Input
            placeholder="Canvas Admin Token"
            value={token}
            onChange={(e) => { setToken(e.target.value); setTestResult(null); }}
            type={showToken ? "text" : "password"}
            className="flex-1"
          />
          <Button type="button" variant="secondary" onClick={() => setShowToken((s) => !s)} title={showToken ? "Hide" : "Show"}>
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button type="button" variant="secondary" onClick={pasteFromClipboard} title="Paste from clipboard">
            <ClipboardPaste className="h-4 w-4" />
          </Button>
        </div>

        {/* Test + result */}
        <div className="flex items-center gap-2">
          <Button onClick={testConnection} disabled={testing || !canvasBaseUrl}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
          {testResult?.user && (
            <div className="text-xs text-gray-700">
              Signed in as <span className="font-mono">{testResult.user.name || testResult.user.login_id || testResult.user.id}</span>
              {testResult.admin_like_access ? " • admin endpoints OK" : " • limited permissions"}
            </div>
          )}
        </div>

        <Button onClick={save} disabled={saving || !testResult?.user || !policyAccepted} className="w-full">
          {saving ? "Saving…" : "Finish"}
        </Button>
      </div>
    </div>
  );
}
